export type RecordingStorageEstimate = {
  usage: number | null
  quota: number | null
}

export type RecordingPersistenceDiagnostics = {
  sequence: number
  blobBytes: number
  quotaExceeded: boolean
  reclaimedChunks: number
  reclaimedBytes: number
  storageBeforeRecovery: RecordingStorageEstimate
  storageAfterRecovery: RecordingStorageEstimate
  initialError: string
  reclaimError: string | null
  retryError: string | null
}

export class RecordingPersistenceError extends Error {
  readonly diagnostics: RecordingPersistenceDiagnostics

  constructor(message: string, diagnostics: RecordingPersistenceDiagnostics, cause: unknown) {
    super(message, { cause })
    this.name = "RecordingPersistenceError"
    this.diagnostics = diagnostics
  }
}

export function isQuotaExceededError(error: unknown): boolean {
  const seen = new Set<unknown>()
  let current: unknown = error

  while (current && !seen.has(current)) {
    seen.add(current)

    if (typeof current === "object") {
      const name = "name" in current && typeof current.name === "string" ? current.name : ""
      const message = "message" in current && typeof current.message === "string" ? current.message : ""

      if (name === "QuotaExceededError" || message.includes("QuotaExceededError")) {
        return true
      }

      if ("cause" in current && current.cause) {
        current = current.cause
        continue
      }

      if ("inner" in current && current.inner) {
        current = current.inner
        continue
      }
    }

    break
  }

  return false
}

export function describeRecordingStorageError(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`
  }

  return String(error)
}

export async function estimateRecordingStorage(): Promise<RecordingStorageEstimate> {
  if (typeof navigator === "undefined" || !navigator.storage?.estimate) {
    return { usage: null, quota: null }
  }

  try {
    const estimate = await navigator.storage.estimate()
    return {
      usage: estimate.usage ?? null,
      quota: estimate.quota ?? null,
    }
  } catch {
    return { usage: null, quota: null }
  }
}
